import React from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Typography,
  LinearProgress,
  Paper
} from '@mui/material';
import { Refresh as RefreshIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useConfig } from './ConfigProvider';

/**
 * Configuration Loader Component
 * Handles loading states, errors, and retry functionality for app configuration
 */
const ConfigLoader = ({ children }) => {
  const { 
    loading, 
    error, 
    config,
    retryCount, 
    reload 
  } = useConfig();

  const isLoading = loading;
  const isError = !!error;
  const isLoaded = !loading && !error && !!config;
  const maxRetries = 3;
  const retryLoadConfig = reload;

  // Show loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          // v1.1: ink full-screen chrome, matching the auth + app-loading screens.
          background: 'var(--as-ink)',
          color: 'white',
          padding: 3
        }}
      >
        <Paper
          elevation={0}
          sx={{
            padding: 4,
            borderRadius: 'var(--as-r-card)',
            textAlign: 'center',
            background: '#ffffff',
            border: '1px solid var(--as-border-soft)',
            color: 'var(--as-t1)',
            minWidth: 300
          }}
        >
          <SettingsIcon sx={{ fontSize: 48, color: 'var(--as-blue-500)', mb: 2 }} />
          
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Loading Configuration
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Connecting to AWS Parameter Store...
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <CircularProgress size={40} thickness={4} />
          </Box>
          
          {retryCount > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Retry attempt {retryCount} of {maxRetries}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(retryCount / maxRetries) * 100} 
                sx={{ mt: 1, borderRadius: 1 }}
              />
            </Box>
          )}
          
          <Typography variant="caption" display="block" sx={{ mt: 2, opacity: 0.7 }}>
            This may take a few moments on first load
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f44336 0%, #ff7961 100%)',
          color: 'white',
          padding: 3
        }}
      >
        <Paper
          elevation={8}
          sx={{
            padding: 4,
            borderRadius: 3,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            color: '#1A1A1A',
            minWidth: 300,
            maxWidth: 500
          }}
        >
          <Alert 
            severity="error" 
            sx={{ mb: 3, textAlign: 'left' }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={retryLoadConfig}
                startIcon={<RefreshIcon />}
                disabled={isLoading}
              >
                Retry
              </Button>
            }
          >
            <Typography variant="h6" gutterBottom>
              Configuration Error
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Alert>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Unable to load application configuration from AWS Parameter Store.
          </Typography>
          
          {retryCount >= maxRetries && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Maximum retry attempts reached. Please check your AWS configuration and network connection.
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={retryLoadConfig}
              startIcon={<RefreshIcon />}
              disabled={isLoading}
              sx={{
                background: 'var(--as-blue-500)',
                '&:hover': { background: 'var(--as-blue-600)' }
              }}
            >
              {isLoading ? 'Retrying...' : 'Retry Now'}
            </Button>
          </Box>
          
          <Typography variant="caption" display="block" sx={{ mt: 3, opacity: 0.7 }}>
            Check browser console for detailed error information
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Configuration loaded successfully - render children
  if (isLoaded) {
    return children;
  }

  // Fallback (shouldn't reach here)
  return null;
};

export default ConfigLoader; 